import {
  ChevronLeft,
  ChevronRight,
  EllipsisVertical,
  Mail,
  Pen,
  Plus,
  Search,
  Trash,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { ActionGateBanner } from '@/components/patterns/ActionGateBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useEmailVerified } from '@/hooks/useEmailVerified';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import { useChangeMemberRole } from '../api/useChangeMemberRole';
import { useListMembers } from '../api/useListMembers';
import { useRemoveMember } from '../api/useRemoveMember';
import { useResendInvitation } from '../api/useResendInvitation';
import { useRoleOptions } from '../api/useRoleOptions';
import { useToggleMemberStatus } from '../api/useToggleMemberStatus';
import { InviteUserModal } from '../components/InviteUserModal';
import type { MemberDto } from '../schemas/memberSchemas';

const PAGE_SIZES = [10, 15, 20, 30, 50];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function UsersScreen() {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isEmailVerified = useEmailVerified();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [changeRoleTarget, setChangeRoleTarget] = useState<MemberDto | null>(null);
  const [changeRoleId, setChangeRoleId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const { data, isLoading } = useListMembers({
    page,
    pageSize,
    search: debouncedSearch || undefined,
  });
  const { data: roles = [] } = useRoleOptions();
  const removeMember = useRemoveMember();
  const changeMemberRole = useChangeMemberRole();
  const resendInvitation = useResendInvitation();
  const toggleStatus = useToggleMemberStatus();

  const members = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  const allSelected = members.length > 0 && members.every((m) => selected.has(m.membershipId));
  const someSelected = members.some((m) => selected.has(m.membershipId));

  function toggleSelectAll() {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(members.map((m) => m.membershipId)));
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleResendInvitation(member: MemberDto) {
    resendInvitation.mutate(member.membershipId, {
      onSuccess: () => toast.success(`Invitation resent to ${member.email}`),
      onError: () => toast.error('Failed to resend invitation'),
    });
  }

  function handleToggleStatus(member: MemberDto) {
    const action = member.isActive ? 'Deactivate' : 'Activate';
    if (!confirm(`${action} ${member.fullName ?? member.email}?`)) return;
    toggleStatus.mutate(member.membershipId, {
      onSuccess: () => toast.success(`User ${action.toLowerCase()}d`),
      onError: () => toast.error(`Failed to ${action.toLowerCase()} user`),
    });
  }

  function handleRemove(member: MemberDto) {
    if (!confirm(`Delete ${member.fullName ?? member.email} from the organization?`)) return;
    removeMember.mutate(member.membershipId, {
      onSuccess: () => toast.success('User deleted'),
      onError: () => toast.error('Failed to delete user'),
    });
  }

  function openChangeRole(member: MemberDto) {
    setChangeRoleTarget(member);
    setChangeRoleId(member.roleId);
  }

  function submitChangeRole() {
    if (!changeRoleTarget) return;
    changeMemberRole.mutate(
      { membershipId: changeRoleTarget.membershipId, roleId: changeRoleId },
      {
        onSuccess: () => {
          toast.success('Role updated');
          setChangeRoleTarget(null);
        },
        onError: () => toast.error('Failed to update role'),
      },
    );
  }

  function renderPageNumbers() {
    const pages: number[] = [];
    const delta = 1;
    const start = Math.max(1, page - delta);
    const end = Math.min(totalPages, page + delta);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  return (
    <AppShell title="Users">
      <div className="flex flex-col gap-4 lg:gap-5 text-black-logo">
        {/* Header */}
        <div className="grid xl:grid-cols-2 gap-6 gap-y-2">
          <div className="space-y-1.5">
            <h4 className="text-lg leading-5.5 font-medium">Listed Users</h4>
            <p className="text-sm leading-[17px] text-gray66">
              {totalCount > 0
                ? `You have ${totalCount} user${totalCount === 1 ? '' : 's'}. Use this section to manage user data, add new users, and maintain an organized user list.`
                : 'Use this section to manage user data, add new users, and maintain an organized user list.'}
            </p>
          </div>
          <div className="flex items-center sm:justify-end max-sm:flex-wrap gap-2.5">
            <div className="flex flex-col gap-1 xl:max-w-[330px] w-full">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute top-1/2 start-4 -translate-y-1/2 text-gray66" />
                <input
                  type="search"
                  className="sc-input input-small ps-9 w-full"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              disabled={!isEmailVerified}
              className="btn btn-primary btn-small max-[480px]:w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-3 h-3" />
              <span>Create User</span>
            </button>
          </div>
        </div>

        {!isEmailVerified && <ActionGateBanner />}

        {/* Table */}
        <div className="space-y-5 lg:space-y-9.5">
          <div className="table-wrapper overflow-x-auto overflow-y-hidden border border-placeholder rounded-lg hide-scrollbar">
            <table className="min-w-full border-collapse text-sm leading-[17px]">
              <thead className="bg-[#F4F6FD] text-black-logo border-b border-[#CBD5E1]">
                <tr>
                  <th className="px-3 py-[8.25px] w-10">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap min-w-40 text-start">
                    User Name
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap min-w-35 text-start">
                    User Role
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Email Id
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Status
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Joined
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#CBD5E1] bg-white font-normal text-gray66">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray66">
                      Loading…
                    </td>
                  </tr>
                ) : members.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray66">
                      {debouncedSearch ? 'No members match your search.' : 'No members yet.'}
                    </td>
                  </tr>
                ) : (
                  members.map((member) => (
                    <tr
                      key={member.membershipId}
                      className={cn(
                        'hover:bg-placeholder/5 transition',
                        selected.has(member.membershipId) && 'bg-[#F0F4FF]',
                      )}
                    >
                      <td className="px-3 py-[11px] w-10">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                          checked={selected.has(member.membershipId)}
                          onChange={() => toggleSelect(member.membershipId)}
                          aria-label={`Select ${member.fullName ?? member.email}`}
                        />
                      </td>
                      <td className="px-3 py-[11px]">
                        <span className="font-medium text-gray30">
                          {member.fullName ?? member.email}
                        </span>
                      </td>
                      <td className="px-3 py-[11px]">{member.roleName}</td>
                      <td className="px-3 py-[11px]">{member.email}</td>
                      <td className="px-3 py-[11px]">
                        <div
                          className={cn(
                            'inline-flex items-center justify-center gap-1 px-2 py-[3px] rounded-full text-xs font-medium border min-w-20',
                            member.isInvitePending
                              ? 'border-placeholder text-placeholder'
                              : member.isActive
                                ? 'border-green00 text-green00'
                                : 'border-red-300 text-red-500',
                          )}
                        >
                          <span>
                            {member.isInvitePending
                              ? 'Pending'
                              : member.isActive
                                ? 'Active'
                                : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-[11px]">{formatDate(member.joinedAt)}</td>
                      <td className="px-3 py-[11px] text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="w-4.5 h-4.5 inline-flex items-center justify-center hover:text-primary cursor-pointer"
                            >
                              <EllipsisVertical className="w-full h-full" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-36">
                            {member.isInvitePending && (
                              <DropdownMenuItem
                                onClick={() => handleResendInvitation(member)}
                                disabled={resendInvitation.isPending || !isEmailVerified}
                                className="flex items-center gap-3 py-1.5 cursor-pointer hover:text-primary"
                              >
                                <Mail className="w-4 h-4" />
                                <span>Resend Invite</span>
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => openChangeRole(member)}
                              disabled={!isEmailVerified}
                              className="flex items-center gap-3 py-1.5 cursor-pointer hover:text-green00"
                            >
                              <Pen className="w-4 h-4" />
                              <span>Change Role</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                member.userId !== currentUserId && handleToggleStatus(member)
                              }
                              disabled={
                                toggleStatus.isPending ||
                                !isEmailVerified ||
                                member.userId === currentUserId
                              }
                              className="flex items-center gap-3 py-1.5 cursor-pointer hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {member.isActive ? (
                                <UserX className="w-4 h-4" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                              <span>{member.isActive ? 'Deactivate' : 'Activate'}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                member.userId !== currentUserId && handleRemove(member)
                              }
                              disabled={
                                removeMember.isPending ||
                                !isEmailVerified ||
                                member.userId === currentUserId
                              }
                              className="flex items-center gap-3 py-1.5 cursor-pointer hover:text-red-600 text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Trash className="w-4 h-4" />
                              <span>Delete User</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex items-center justify-between max-sm:flex-wrap gap-y-3 gap-4 text-sm leading-[17px] text-black-light">
              <p>
                Showing {rangeStart} – {rangeEnd} of {totalCount} Users
              </p>
              <div className="flex items-center gap-2 text-black max-sm:justify-center max-sm:w-full max-sm:order-last">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="min-h-5.5 min-w-5 border border-grayEF rounded-sm py-px px-1 disabled:bg-grayEF disabled:text-gray66"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                {renderPageNumbers().map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={cn(
                      'min-h-5.5 min-w-5 border border-grayEF rounded-sm py-px px-1',
                      p === page && 'border-primary text-primary',
                    )}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="min-h-5.5 min-w-5 border border-grayEF rounded-sm py-px px-1 disabled:bg-grayEF disabled:text-gray66"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span>Show</span>
                <select
                  className="sc-select border-[#CCDAFF] focus:border-primary py-0.5 px-[8px] bg-[right_4px_center] rounded-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <InviteUserModal open={inviteOpen} onClose={() => setInviteOpen(false)} roles={roles} />

      {/* Change Role Modal */}
      {changeRoleTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/45"
            onClick={() => setChangeRoleTarget(null)}
            aria-hidden="true"
          />
          <div className="flex justify-center p-5 w-full">
            <div className="relative w-full max-w-sm rounded-3xl p-5 lg:p-7.5 flex flex-col gap-5 bg-white border border-placeholder shadow-[0px_0px_18.6px_0px_#0000001A] animate-modal text-black-logo tracking-tight">
              <div className="flex items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-black tracking-normal">Change Role</h2>
                <button
                  type="button"
                  onClick={() => setChangeRoleTarget(null)}
                  className="w-6 h-6 rounded-full bg-[#E5EDFF] text-primary flex items-center justify-center shrink-0 hover:bg-[#d7e3ff] transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray66 -mt-2">
                Updating role for{' '}
                <span className="font-medium text-gray30">
                  {changeRoleTarget.fullName ?? changeRoleTarget.email}
                </span>
              </p>
              <div className="flex flex-col gap-1">
                <label htmlFor="change-role-select" className="sc-input-label">
                  New Role
                </label>
                <select
                  id="change-role-select"
                  className="sc-select"
                  value={changeRoleId}
                  onChange={(e) => setChangeRoleId(e.target.value)}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end items-center gap-3.5">
                <button
                  type="button"
                  onClick={() => setChangeRoleTarget(null)}
                  className="btn btn-outlined btn-small text-primary! hover:bg-primary/10!"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    changeMemberRole.isPending ||
                    changeRoleId === changeRoleTarget.roleId ||
                    !isEmailVerified
                  }
                  onClick={submitChangeRole}
                  className="btn btn-primary btn-small"
                >
                  {changeMemberRole.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
